import React, { useState, useEffect, useMemo, useCallback, useRef, Suspense } from 'react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Plus, RefreshCw, AlertCircle, ChevronDown, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { mcmService, Control, ControlStatus, BulkOperation } from '@/services/mcmService';
import { db } from '@/lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import SearchBar from './components/SearchBar';
import ControlList from './components/ControlList';
import ActionBar from './components/ActionBar';
import AddControlModal from './components/AddControlModal';
import BulkOperationsModal from './components/BulkOperationsModal'
import ErrorBoundary from './components/ErrorBoundary';
import { FullPageSkeleton } from './components/LoadingSkeleton';
import { useTenancy } from '@/contexts/TenancyContext';

// Maximum number of tabs to show before using dropdown
const MAX_TABS = 5;

// Main MCM component with error boundary and suspense
const MCMContent = () => {
  const { activeTenancy, enabledModules } = useTenancy();
  const [controls, setControls] = useState<Control[]>([]);
  const [filteredControls, setFilteredControls] = useState<Control[]>([]);
  const [selectedControls, setSelectedControls] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<string>('all');
  const [standards, setStandards] = useState<string[]>([]); 
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize the fetch function
  const fetchControls = useCallback(async () => {
    try {
      if (!activeTenancy?.id || activeTenancy?.id === 'Loading...' || activeTenancy?.id === '') return;
      const data = await mcmService.getControls(false, activeTenancy?.id);
      console.log('MCM Data: ', data);

      // Flatten ControlsResponse into a per-control array and enrich linkedComponents
      const flattened: Control[] = [];

      const standardsArr: any[] = Array.isArray((data as any)?.Controls) ? (data as any).Controls : [];
      for (const stdObj of standardsArr) {
        const std = (stdObj as any)?.standard;
        for (const [key, value] of Object.entries(stdObj)) {
          if (key === 'standard') continue;
          const merged = value as any;
          if (!merged) continue;

          // Start with merged fields from service and ensure identifiers
          const base: any = {
            id: merged.controlID || key,
            controlID: merged.controlID || key,
            standard: merged.standard || std,
            ...merged,
          };

          // Normalize legacy arrays into linkedComponents for parity with MCMMappingsCard
          const links: any[] = Array.isArray(base.linkedComponents) ? [...base.linkedComponents] : [];

          const dedupe = new Set<string>(
            links.map((l: any) => `${String(l.domain || '').toLowerCase()}|${String(l.ref || l.id || '')}`)
          );

          const addLink = (domain: string, refRaw: any) => {
            const ref = String(refRaw || '').trim();
            if (!ref) return;
            const dom = domain[0].toUpperCase() + domain.slice(1).toLowerCase();
            const keySig = `${dom.toLowerCase()}|${ref}`;
            if (dedupe.has(keySig)) return;
            dedupe.add(keySig);
            links.push({
              id: `internal:${dom.toLowerCase()}:${ref}`,
              domain: dom,
              scheme: 'internal',
              ref,
              title: '',
              relation: 'related',
              confidence: 1,
            });
          };

          // Legacy arrays -> normalized links
          (Array.isArray(base.supportingDocuments) ? base.supportingDocuments : []).forEach((r: any) => addLink('Document', r));
          (Array.isArray(base.documents) ? base.documents : []).forEach((r: any) => addLink('Document', r));
          // DED-1492: Include evidence array as Evidence domain
          (Array.isArray(base.evidence) ? base.evidence : []).forEach((r: any) => addLink('Evidence', r));
          (Array.isArray(base.assets) ? base.assets : []).forEach((r: any) => addLink('Asset', r));
          (Array.isArray(base.policies) ? base.policies : []).forEach((r: any) => addLink('Policy', r));
          (Array.isArray(base.risks) ? base.risks : []).forEach((r: any) => addLink('Risk', r));
          (Array.isArray(base.tasks) ? base.tasks : []).forEach((r: any) => addLink('Assurance', r));
          (Array.isArray(base.relatedControls) ? base.relatedControls : []).forEach((r: any) => addLink('Control', r));

          base.linkedComponents = links;
          flattened.push(base as Control);
        }
      }

      // Also load all controls stored under /customers/{id}/mcm/*/controls/* and merge
      try {
        const stdCol = collection(db, 'customers', activeTenancy.id, 'mcm');
        const stdSnap = await getDocs(stdCol);
        const mergedByKey = new Map<string, any>();
        // Seed with flattened
        for (const c of flattened) {
          const key = `${String((c as any).standard || '')}|${String((c as any).controlID || (c as any).id || '')}`;
          mergedByKey.set(key, { ...(c as any) });
        }
        for (const stdDoc of stdSnap.docs) {
          const stdId = stdDoc.id;
          const ctrlsCol = collection(db, 'customers', activeTenancy.id, 'mcm', stdId, 'controls');
          const ctrlsSnap = await getDocs(ctrlsCol);
          for (const ctrlDoc of ctrlsSnap.docs) {
            const cdata = ctrlDoc.data() as any;
            const ctlId = cdata?.controlID || ctrlDoc.id;
            if (!ctlId) continue;
            const key = `${String(cdata?.standard || stdId)}|${String(ctlId)}`;
            const existing = mergedByKey.get(key) || {
              id: ctlId,
              controlID: ctlId,
              standard: cdata?.standard || stdId,
            };
            // Merge, prioritizing MCM fields for linkedComponents
            const base: any = { ...existing, ...cdata };

            // Normalize legacy arrays to links and union with any existing linkedComponents
            const links: any[] = [];
            const pushLink = (dom: string, refRaw: any) => {
              const ref = String(refRaw || '').trim();
              if (!ref) return;
              links.push({
                id: `internal:${dom.toLowerCase()}:${ref}`,
                domain: dom[0].toUpperCase() + dom.slice(1).toLowerCase(),
                scheme: 'internal',
                ref,
                title: '',
                relation: 'related',
                confidence: 1,
              });
            };
            (Array.isArray(base.supportingDocuments) ? base.supportingDocuments : []).forEach((r: any) => pushLink('Document', r));
            (Array.isArray(base.documents) ? base.documents : []).forEach((r: any) => pushLink('Document', r));
            // DED-1492: Include evidence array as Evidence domain
            (Array.isArray(base.evidence) ? base.evidence : []).forEach((r: any) => pushLink('Evidence', r));
            (Array.isArray(base.assets) ? base.assets : []).forEach((r: any) => pushLink('Asset', r));
            (Array.isArray(base.policies) ? base.policies : []).forEach((r: any) => pushLink('Policy', r));
            (Array.isArray(base.risks) ? base.risks : []).forEach((r: any) => pushLink('Risk', r));
            (Array.isArray(base.tasks) ? base.tasks : []).forEach((r: any) => pushLink('Assurance', r));
            (Array.isArray(base.relatedControls) ? base.relatedControls : []).forEach((r: any) => pushLink('Control', r));

            const existingLinks = Array.isArray(existing?.linkedComponents) ? existing.linkedComponents : [];
            const mcmLinks = Array.isArray(cdata?.linkedComponents) ? cdata.linkedComponents : [];
            const union = [...existingLinks, ...mcmLinks, ...links];
            const seen = new Set<string>();
            base.linkedComponents = union.filter((l: any) => {
              const sig = `${String(l?.domain || '').toLowerCase()}|${String(l?.ref || l?.id || '')}`;
              if (!sig.trim() || seen.has(sig)) return false;
              seen.add(sig); return true;
            });

            mergedByKey.set(key, base);
          }
        }
        // Enrich each control by fetching authoritative MCM doc and union links
        const mergedArray = Array.from(mergedByKey.values()) as any[];
        const enriched = await Promise.all(
          mergedArray.map(async (base) => {
            try {
              const fresh = await mcmService.getControlById(String(base.controlID || base.id), activeTenancy.id);
              if (!fresh) return base;
              const existingLinks = Array.isArray(base?.linkedComponents) ? base.linkedComponents : [];
              const mcmLinks = Array.isArray((fresh as any)?.linkedComponents) ? (fresh as any).linkedComponents : [];
              const norm: any[] = [];
              const add = (dom: string, refRaw: any) => {
                const ref = String(refRaw || '').trim();
                if (!ref) return;
                norm.push({
                  id: `internal:${dom.toLowerCase()}:${ref}`,
                  domain: dom[0].toUpperCase() + dom.slice(1).toLowerCase(),
                  scheme: 'internal',
                  ref,
                  title: '',
                  relation: 'related',
                  confidence: 1,
                });
              };
              const f = fresh as any;
              (Array.isArray(f.supportingDocuments) ? f.supportingDocuments : []).forEach((r: any) => add('Document', r));
              (Array.isArray(f.documents) ? f.documents : []).forEach((r: any) => add('Document', r));
              // DED-1492: Include evidence array as Evidence domain
              (Array.isArray(f.evidence) ? f.evidence : []).forEach((r: any) => add('Evidence', r));
              (Array.isArray(f.assets) ? f.assets : []).forEach((r: any) => add('Asset', r));
              (Array.isArray(f.policies) ? f.policies : []).forEach((r: any) => add('Policy', r));
              (Array.isArray(f.risks) ? f.risks : []).forEach((r: any) => add('Risk', r));
              (Array.isArray(f.tasks) ? f.tasks : []).forEach((r: any) => add('Assurance', r));
              (Array.isArray(f.relatedControls) ? f.relatedControls : []).forEach((r: any) => add('Control', r));

              const union = [...existingLinks, ...mcmLinks, ...norm];
              const seen = new Set<string>();
              base.linkedComponents = union.filter((l: any) => {
                const sig = `${String(l?.domain || '').toLowerCase()}|${String(l?.ref || l?.id || '')}`;
                if (!sig.trim() || seen.has(sig)) return false;
                seen.add(sig); return true;
              });
              // Prefer fresh standard/fields if missing
              if (!base.standard && (fresh as any)?.standard) base.standard = (fresh as any).standard;
              return base;
            } catch {
              return base;
            }
          })
        );
        setControls(enriched as Control[]);
      } catch (mcmLoadErr) {
        console.warn('[MCM] Failed to load /customers/{id}/mcm/*/controls/*; falling back to modules/SOA only', mcmLoadErr);
        setControls(flattened);
      }
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch controls';
      setError(errorMessage);
      throw err; // Let the error boundary handle this
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [activeTenancy]);

  // Initial fetch with error boundary
  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        await fetchControls();
      } catch (err) {
        if (mounted) {
          console.error('Failed to load controls:', err);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [fetchControls]);

  // Extract unique standards from controls
  useEffect(() => {
    const controlsArray = Array.isArray(controls) ? controls : [];
    const uniqueStandards = Array.from(
      new Set(controlsArray.map((control: any) => control.standard).filter(Boolean))
    ).sort();
    setStandards(['all', ...uniqueStandards]);
  }, [controls]);

  // Filter controls based on search query and active tab (standard)
  // Natural sort function for control IDs (handles A.5.1, A.5.10, DISP001, ISM-4093, etc.)
  const naturalSortControlIDs = (a: any, b: any) => {
    // Function to split ID into parts (letters, numbers, separators)
    const parseControlID = (id: string = '') => {
      // Break the ID into chunks of letters, numbers, and separators
      const parts = id.match(/([a-zA-Z]+|\d+|[^a-zA-Z0-9]+)/g) || [];
      return parts.map(part => {
        // Return number parts as actual numbers for proper numerical comparison
        if (/^\d+$/.test(part)) {
          return parseInt(part, 10);
        }
        return part;
      });
    };

    const aID = a.controlID || a.id || '';
    const bID = b.controlID || b.id || '';

    const aParts = parseControlID(aID);
    const bParts = parseControlID(bID);

    // Compare each part
    const maxLength = Math.max(aParts.length, bParts.length);
    for (let i = 0; i < maxLength; i++) {
      // If we've reached the end of one array, the shorter one comes first
      if (i >= aParts.length) return -1;
      if (i >= bParts.length) return 1;

      // Different comparison based on type
      if (typeof aParts[i] === 'number' && typeof bParts[i] === 'number') {
        // Compare numbers numerically
        if (aParts[i] !== bParts[i]) {
          return aParts[i] - bParts[i];
        }
      } else {
        // Compare strings or mixed types alphabetically
        const aStr = String(aParts[i]).toLowerCase();
        const bStr = String(bParts[i]).toLowerCase();
        if (aStr !== bStr) {
          return aStr < bStr ? -1 : 1;
        }
      }
    }
    
    return 0; // IDs are identical
  };

  // Check if ISO 27001 module is enabled
  const isISO27001Enabled = enabledModules?.includes('iso27001') || enabledModules?.includes('iso27001-2013');

  useEffect(() => {
    console.log('controls', controls);
    // Work directly with flattened per-control array
    let result = Array.isArray(controls) ? [...controls] : [];

    // If no controls found, return early
    if (result.length === 0) {
      setFilteredControls([]);
      return;
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((control: any) => {
        const name = String(control.name || '').toLowerCase();
        const ctrlName = String(control.controlName || '').toLowerCase();
        const desc = String(control.controlDescription || '').toLowerCase();
        const id = String(control.controlID || control.id || '').toLowerCase();
        const acsc = String(control.acscControl || '').toLowerCase();
        return (
          name.includes(query) ||
          ctrlName.includes(query) ||
          desc.includes(query) ||
          id.includes(query) ||
          acsc.includes(query)
        );
      });
    }

    // Apply standard filter if needed
    if (activeTab !== 'all') {
      result = result.filter((control) => control.standard === activeTab);
    }
    
    // Filter out empty controls
    result = result.filter((control) => {
      // Control is not empty if it has required fields
      return (
        control.controlID || 
        control.controlName || 
        control.controlDescription
      );
    });
    
    // First sort controls by ID using natural sorting to establish base order
    result.sort(naturalSortControlIDs);

    // Then prioritize ISO 27001 controls if that module is enabled
    if (isISO27001Enabled) {
      // Split into ISO 27001 and other controls
      const iso27001Controls = result.filter(control => {
        const standard = (control.standard || '').toLowerCase();
        return standard.includes('iso') && standard.includes('27001');
      });
      
      const otherControls = result.filter(control => {
        const standard = (control.standard || '').toLowerCase();
        return !standard.includes('iso') || !standard.includes('27001');
      });
      
      // Join the arrays with ISO 27001 controls first
      result = [...iso27001Controls, ...otherControls];
    }

    setFilteredControls(result);
  }, [controls, searchQuery, activeTab]);

  // Handle selecting a control (toggle selection)  
  const handleSelectControl = useCallback((id: string, checked: boolean) => {
    console.log(`Selection change: ${id} => ${checked ? 'selected' : 'deselected'}`);
    setSelectedControls(prev => {
      if (checked) {
        return [...prev, id];
      } else {
        return prev.filter(controlId => controlId !== id);
      }
    });
  }, []);

  // Handle select all/none
  const handleSelectAll = (checked: boolean) => {
    setSelectedControls(checked ? filteredControls.map((control) => control.id) : []);
  };

  // Key to force ControlList to completely re-render when needed
  const [controlListKey, setControlListKey] = useState<number>(Date.now());

  const handleClearSelection = () => {
    // Instead of trying complex solutions, directly set the selection to empty
    // and force a refresh of the ControlList component with a new key
    setSelectedControls([]); 
    setControlListKey(Date.now()); // Use a new key to force full component refresh
  };

  // Handle control creation
  const handleAddControl = async (controlData: Omit<Control, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (!activeTenancy?.id) {
        throw new Error('No active tenancy selected');
      }
      setIsLoading(true);
      await mcmService.createControl(controlData, activeTenancy.id);
      await fetchControls();
      setIsAddModalOpen(false);
      toast.success('Control created successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create control';
      toast.error(errorMessage);
      throw err; // Let the error boundary handle this
    } finally {
      setIsLoading(false);
    }
  };

  // Handle bulk operations
  const handleBulkOperation = async (operation: Omit<BulkOperation, 'controlIds'>) => {
    try {
      if (!activeTenancy?.id) {
        throw new Error('No active tenancy selected');
      }
      
      // Filter out any undefined or empty control IDs
      const validControlIds = selectedControls.filter(id => id && typeof id === 'string' && id.trim() !== '');
      
      if (validControlIds.length === 0) {
        toast.warning('No valid controls selected for bulk operation');
        return;
      }
      
      console.log('Performing bulk operation with valid IDs:', validControlIds);
      setIsLoading(true);
      
      await mcmService.bulkUpdate({
        ...operation,
        controlIds: validControlIds,
      }, activeTenancy.id);

      await fetchControls();
      setSelectedControls([]);
      setIsBulkModalOpen(false);

      toast.success(`Bulk operation completed on ${selectedControls.length} controls`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to perform bulk operation';
      toast.error(errorMessage);
      throw err; // Let the error boundary handle this
    } finally {
      setIsLoading(false);
    }
  };

  // Handle control deletion
  const handleDeleteControls = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedControls.length} selected controls?`)) {
      return;
    }

    try {
      if (!activeTenancy?.id) {
        throw new Error('No active tenancy selected');
      }
      setIsLoading(true);
      await Promise.all(selectedControls.map((id) => mcmService.deleteControl(id, activeTenancy.id)));
      await fetchControls();
      setSelectedControls([]);

      toast.success(`Deleted ${selectedControls.length} controls`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete controls';
      toast.error(errorMessage);
      throw err; // Let the error boundary handle this
    } finally {
      setIsLoading(false);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchControls().catch(console.error);
  };

  // Error state UI
  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="rounded-md bg-destructive/10 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-destructive">Error loading controls</h3>
              <div className="mt-2 text-sm text-destructive">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Master Control Mapping</h1>
          <p className="text-muted-foreground">
            Manage your security controls and their relationships
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
            disabled={isLoading}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Control
          </Button>
        </div>
      </div>

      <div className="space-y-4 w-full">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <SearchBar
            placeholder="Search controls..."
            onSearch={setSearchQuery}
            className="w-full max-w-xl"
            disabled={isLoading}
          />

          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value)}
            className="w-full md:w-auto"
          >
            <TabsList className="flex w-full">
              {/* Show first MAX_TABS tabs directly */}
              {standards.slice(0, MAX_TABS).map(standard => (
                <TabsTrigger key={standard} value={standard} className="flex-1 transition-all data-[state=active]:bg-primary data-[state=active]:text-white">
                  {standard === 'all' ? 'All' : standard.charAt(0).toUpperCase() + standard.slice(1)}
                </TabsTrigger>
              ))}
              
              {/* Show dropdown for additional standards if there are more than MAX_TABS */}
              {standards.length > MAX_TABS && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <div className="flex-none">
                      <TabsTrigger 
                        value={standards.slice(MAX_TABS).includes(activeTab) ? activeTab : "more"} 
                        className="flex-none px-3 gap-1 h-9 transition-all data-[state=active]:bg-primary data-[state=active]:text-white"
                      >
                        {standards.slice(MAX_TABS).includes(activeTab) ? 
                          <div className="flex items-center gap-1">
                            <span>{activeTab === 'all' ? 'All' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</span>
                            <ChevronDown className="h-3 w-3 opacity-70" />
                          </div> : 
                          <>
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only md:not-sr-only md:inline-block ml-1">More</span>
                          </>
                        }
                      </TabsTrigger>
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    {standards.slice(MAX_TABS).map(standard => (
                      <DropdownMenuItem 
                        key={standard}
                        onClick={() => setActiveTab(standard)}
                        className={activeTab === standard ? "bg-accent text-accent-foreground" : ""}
                      >
                        {standard === 'all' ? 'All' : standard.charAt(0).toUpperCase() + standard.slice(1)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </TabsList>
          </Tabs>
        </div>

        {selectedControls.length > 0 && (
          <ActionBar
            selectedCount={selectedControls.length}
            onBulkEdit={() => setIsBulkModalOpen(true)}
            onDelete={handleDeleteControls}
            onClearSelection={handleClearSelection}
            disabled={isLoading}
          />
        )}

        <ControlList
          key={controlListKey}
          controls={filteredControls}
          selectedControls={selectedControls}
          onSelectControl={handleSelectControl}
          onSelectAll={handleSelectAll}
          someSelected={selectedControls.length > 0}
          allSelected={selectedControls.length === filteredControls.length}
          isLoading={isLoading}
          error={error}
        />

      </div>

      <AddControlModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddControl}
        isLoading={isLoading}
      />

      <BulkOperationsModal
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        onSubmit={handleBulkOperation}
        selectedCount={selectedControls.length}
        isLoading={isLoading}
        selectedControlIds={selectedControls}
      />
    </div>
  );
};

// Main export with error boundary and suspense
const MasterControlMapping = () => {
  return (
    <ErrorBoundary
      fallback={
        <div className="container mx-auto p-6">
          <div className="rounded-md bg-destructive/10 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-destructive">
                  Something went wrong
                </h3>
                <div className="mt-2 text-sm text-destructive">
                  <p>An unexpected error occurred while loading the Master Control Mapping.</p>
                </div>
                <div className="mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.reload()}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Reload Page
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <Suspense fallback={<FullPageSkeleton />}>
        <MCMContent />
      </Suspense>
    </ErrorBoundary>
  );
};

export default MasterControlMapping;